import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n";
import api from "@/lib/api";
import { toast } from "sonner";

export default function BookingDialog({ trigger, defaultHotelId, triggerLabel }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    whatsapp: "",
    email: "",
    arrival_date: "",
    departure_date: "",
    guests: 1,
    birthday: "",
    notes: "",
  });

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        guests: Number(form.guests) || 1,
        hotel_id: defaultHotelId,
      };
      if (!payload.email) delete payload.email;
      if (!payload.birthday) delete payload.birthday;
      await api.post("/bookings", payload);
      toast.success(t("form.thanks"));
      setOpen(false);
      setForm({ name: "", phone: "", whatsapp: "", email: "", arrival_date: "", departure_date: "", guests: 1, birthday: "", notes: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-brand-orange hover:bg-brand-orange-dark text-white rounded-full" data-testid="open-booking-dialog">
            {triggerLabel || t("cta.inquire")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg bg-canvas-cream" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand-navy">{t("cta.inquire")}</DialogTitle>
          <DialogDescription className="text-brand-navy/70">
            {t("hero.subtitle")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="name">{t("form.name")}</Label>
            <Input id="name" required value={form.name} onChange={onChange("name")} data-testid="booking-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="phone">{t("form.phone")}</Label>
              <Input id="phone" required value={form.phone} onChange={onChange("phone")} data-testid="booking-phone" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="wa">{t("form.whatsapp")}</Label>
              <Input id="wa" value={form.whatsapp} onChange={onChange("whatsapp")} data-testid="booking-whatsapp" />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="email">{t("form.email")}</Label>
            <Input id="email" type="email" value={form.email} onChange={onChange("email")} data-testid="booking-email" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="arr">{t("form.arrival")}</Label>
              <Input id="arr" type="date" value={form.arrival_date} onChange={onChange("arrival_date")} data-testid="booking-arrival" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="dep">{t("form.departure")}</Label>
              <Input id="dep" type="date" value={form.departure_date} onChange={onChange("departure_date")} data-testid="booking-departure" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="g">{t("form.guests")}</Label>
              <Input id="g" type="number" min="1" value={form.guests} onChange={onChange("guests")} data-testid="booking-guests" />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="birthday">{t("form.birthday")}</Label>
            <Input id="birthday" type="date" value={form.birthday} onChange={onChange("birthday")} data-testid="booking-birthday" />
            <p className="text-[11px] text-slate-500">{t("form.birthday_hint")}</p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="n">{t("form.notes")}</Label>
            <Textarea id="n" rows={3} value={form.notes} onChange={onChange("notes")} data-testid="booking-notes" />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={loading}
              className="bg-brand-navy hover:bg-brand-navy-dark text-white rounded-full px-8"
              data-testid="booking-submit-button"
            >
              {loading ? "..." : t("cta.send")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
